import styles from "../replacements-page-styles.module.css";
import ReplacementsHeader from "components/Replacements/ReplacementsHeader";
import ReplacementReview from "components/Replacements/components/ReplacementReview/ReplacementReview.tsx";
import { useNavigate, useParams } from "react-router";
import { useGetByIdQuery } from "features/api/orderApi.ts";
import ReplacementReviewEditOrderDetails from "components/Replacements/components/ReplacementReviewEditOrderDetails/ReplacementReviewEditOrderDetails.tsx";
import ReplacementReviewButtons from "components/Replacements/components/ReplacementReviewButtons/ReplacementReviewButtons.tsx";
import { useState } from "react";
import OrderDetailsHeaderSkeleton from "components/Skeletons/OrderDetailsHeaderSkeleton";
import OrderDetailsSummarySkeleton from "components/Skeletons/OrderDetailsSummarySkeleton";
import OrderDetailsLineItemSkeleton from "components/Skeletons/OrderDetailsLineItemSkeleton";
import ReplacementShipTo from "components/Replacements/ReplacementShipTo";
import { APIErrorResponse } from "features/api/utils/apiUtils.ts";
import { OrderType } from "types/declarations/enums.ts";
import { useGetAccountsByIdQuery } from "features/api/accountApi.ts";
import BillToBox from "components/Common/BillToShipToBox/BillToBox/BillToBox";
import {
	EditText,
	GenericOrderErrorMaintenanceSubHeader,
	NewOrderErrorMaintenanceHeader,
	ReplacementPost400ErrorHeader4000Errors
} from "constants/text.ts";
import ErrorBanner from "components/Common/ErrorBanner/ErrorBanner.tsx";
import LinkButton from "components/Common/Link/LinkButton.tsx";
import { useSelector } from "react-redux";
import { RootState } from "stores/application.store.tsx";
import {
	ReplacementReviewDetailsDivider,
	ReplacementReviewDetailsWrapper,
	ReplacementReviewPageContent
} from "./ReplacementsReviewPage.styles.ts";

const handleErrorMessaging = (errors: APIErrorResponse["errors"]) => {
	const isMaintenanceError = errors && errors[0]?.code === 40002;
	return (
		<div>
			<div>{isMaintenanceError ? NewOrderErrorMaintenanceHeader : ReplacementPost400ErrorHeader4000Errors}</div>
			{isMaintenanceError && <div>{GenericOrderErrorMaintenanceSubHeader}</div>}
			{!isMaintenanceError && (
				<ul>
					{errors?.map((error) => (
						<li
							data-testid="api-error-banner-message"
							key={error.code}
						>
							{error.message}
						</li>
					))}
				</ul>
			)}
			{isMaintenanceError && <div>(40002)</div>}
		</div>
	);
};

const ReplacementReviewPage = () => {
	const params = useParams();
	const navigate = useNavigate();
	const { data } = useGetByIdQuery(params?.orderId ?? "", { skip: !params?.orderId });
	const [postError, setPostError] = useState<APIErrorResponse["errors"]>();

	const replacementOrderBillTo = useSelector(
		(state: RootState) => state.submittingReplacementOrder.order?.billingDetails
	);

	const handleReplacementError = (error: APIErrorResponse["errors"]) => {
		setPostError(error);
	};

	const { data: accounts, isLoading } = useGetAccountsByIdQuery(params?.orderId ?? "");

	if (isLoading) {
		return (
			<div data-testid="order-detail-page-skeleton">
				<OrderDetailsHeaderSkeleton />
				<OrderDetailsSummarySkeleton />
				{new Array(6).fill(1).map((index: any) => (
					<OrderDetailsLineItemSkeleton key={index} />
				))}
			</div>
		);
	}

	const ReplacementReviewDetails = [
		<BillToBox
			billTo={replacementOrderBillTo}
			orderType={OrderType.REPLACEMENT}
			isNoChargeOrder
			dataTestIdPrefix="replacement"
			key="0"
		/>,
		<ReplacementReviewEditOrderDetails
			orderDetail={data}
			key="1"
		/>,
		<ReplacementShipTo
			accounts={accounts?.[0]}
			orderDetail={data}
			key="2"
		/>,
		<LinkButton
			data-testid="replacement-shipping-details-edit-button"
			onClick={() => {
				navigate(`/replacements/shipping-details/${params?.orderId}`);
			}}
			key="3"
			style={{ position: "absolute", top: "18px", right: "16px" }}
		>
			{EditText}
		</LinkButton>
	];

	return (
		<div>
			<ReplacementsHeader orderDetail={data} />
			<ReplacementReviewPageContent>
				{postError && (
					<div className={styles.replacementReasonContainer}>
						<ErrorBanner errorMessaging={handleErrorMessaging(postError)} />
					</div>
				)}
				<ReplacementReview />
				<ReplacementReviewDetailsWrapper>
					{ReplacementReviewDetails.map((detail, index) => (
						<div key={detail.key}>
							{detail}
							{index !== ReplacementReviewDetails.length - 1 &&
								index !== ReplacementReviewDetails.length - 2 && <ReplacementReviewDetailsDivider />}
						</div>
					))}
				</ReplacementReviewDetailsWrapper>
				<ReplacementReviewButtons
					orderDetail={data}
					accounts={accounts?.[0]}
					handleReplacementError={handleReplacementError}
				/>
			</ReplacementReviewPageContent>
		</div>
	);
};

export default ReplacementReviewPage;
